import React, { useEffect, useState } from 'react';
import ReactPaginate from 'react-paginate';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCodes, createCode, updateCode, deleteCode } from '../../store/actions/codeActions';
import { toast } from 'sonner';
import AdvancedOptionEditor from './AdvancedOptionEditor';
import {
  Btn,
  BmModal,
  Card,
  ConfirmModal,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Loading,
  SearchBox,
} from '../ForBrands/ui/BrandUI';
import { AddRow, CardHead, IconButton } from './codeUi';

// Admin Mode > Codes, right card: the codes of the selected option in the V2
// table style, with an add row, a name filter and client side pages (the
// school and team options run long). Each row renames in a BmModal, opens the
// option editor, or asks before it deletes.

export const CODES_PAGE_SIZE = 25;

const EMPTY = [];

const optionCount = (code) => (
  code?.options && typeof code.options === 'object' && !Array.isArray(code.options)
    ? Object.keys(code.options).length
    : 0
);

const TH = 'whitespace-nowrap border-b border-bm-border px-3 py-2.5 bm-label-xs uppercase tracking-[0.06em] text-bm-muted';
const TD = 'px-3 py-2.5 bm-body-m text-bm-ink';

const CodesTable = ({ selectedOption }) => {
  const dispatch = useDispatch();
  const { codes, codeOptions } = useSelector((state) => state.codes);
  const [newCodeName, setNewCodeName] = useState('');
  const [editingCode, setEditingCode] = useState(null);
  const [editedName, setEditedName] = useState('');
  const [showOptionsDialog, setShowOptionsDialog] = useState(null);
  const [deletingCode, setDeletingCode] = useState(null);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  // The option whose codes have finished loading, so a switch shows Loading
  // rather than an empty list while the fetch runs.
  const [loadedFor, setLoadedFor] = useState(null);

  useEffect(() => {
    if (selectedOption && Object.keys(selectedOption).length > 0) {
      const id = selectedOption._id;
      let live = true;
      Promise.resolve(dispatch(fetchCodes(selectedOption._id))).finally(() => {
        if (live) setLoadedFor(id);
      });
      return () => {
        live = false;
      };
    }
    return undefined;
  }, [dispatch, selectedOption]);

  // A new option starts on the first page with no filter.
  const selectedId = selectedOption?._id;
  useEffect(() => {
    setQuery('');
    setPage(0);
  }, [selectedId]);

  const loadFailed = codes != null && !Array.isArray(codes);
  const filteredCodes = (Array.isArray(codes) ? codes : EMPTY).filter((code) => code.codeOptionID === selectedOption?._id);

  const handleAddCode = () => {
    if (!newCodeName.trim()) return;

    dispatch(createCode({
      name: newCodeName,
      codeOptionID: selectedOption._id,
    }));
    setNewCodeName('');
    toast.success('Code created');
  };

  const handleUpdateCode = (code) => {
    setEditingCode(code);
    setEditedName(code.name);
  };

  const handleDeleteCode = (code) => {
    dispatch(deleteCode(code)).then(() => {
      dispatch(fetchCodes(selectedOption._id));
      toast.success('Code deleted');
    });
  };

  const handleConfirmUpdate = (nameChanged, optionsChanged, opts) => {
    if (nameChanged && !editedName.trim()) {
      return;
    }
    if (optionsChanged && !Object.keys(opts).length) {
      return;
    }
    const updates = [];

    if (nameChanged) {
      updates.push(dispatch(updateCode({ ...editingCode, name: editedName })));
      setEditingCode(null);
    }

    if (optionsChanged) {
      updates.push(dispatch(updateCode({ ...showOptionsDialog, options: opts })));
      setShowOptionsDialog(null);
    }

    Promise.all(updates).then(() => {
      dispatch(fetchCodes(selectedOption._id));
      toast.success('Code updated');
    });
  };

  if (!selectedOption) {
    return (
      <Card padding="p-5" className="flex min-w-0 flex-col gap-4">
        <CardHead title="Codes" />
        <EmptyState>Select a code option to see and add its codes.</EmptyState>
      </Card>
    );
  }

  // Show the option's current name, so a rename on the left shows here too.
  const option = (Array.isArray(codeOptions) && codeOptions.find((o) => o._id === selectedOption._id)) || selectedOption;
  const needle = query.trim().toLowerCase();
  const shown = needle
    ? filteredCodes.filter((code) => String(code.name ?? '').toLowerCase().includes(needle))
    : filteredCodes;
  const pageCount = Math.ceil(shown.length / CODES_PAGE_SIZE);
  const safePage = Math.min(page, Math.max(pageCount - 1, 0));
  const pageRows = shown.slice(safePage * CODES_PAGE_SIZE, (safePage + 1) * CODES_PAGE_SIZE);
  const loadingCodes = loadedFor !== selectedOption._id && filteredCodes.length === 0;

  let body;
  if (loadFailed) {
    body = <ErrorState>Could not load the codes for this option. Select it again to retry.</ErrorState>;
  } else if (loadingCodes) {
    body = <Loading label="Loading codes..." />;
  } else if (filteredCodes.length === 0) {
    body = <EmptyState>No codes in {option.name} yet. Add the first one above.</EmptyState>;
  } else {
    body = (
      <>
        <SearchBox
          value={query}
          onChange={(v) => {
            setQuery(v);
            setPage(0);
          }}
          placeholder={`Filter ${filteredCodes.length} ${filteredCodes.length === 1 ? 'code' : 'codes'} by name`}
          aria-label="Filter codes by name"
          className="min-w-0"
          subtle
        />
        {shown.length === 0 ? (
          <EmptyState>No codes match "{query.trim()}".</EmptyState>
        ) : (
          <div className="w-full max-w-full overflow-x-auto rounded-[8px] border border-bm-border">
            <table className="w-full text-left">
              <thead className="bg-bm-subtle">
                <tr>
                  <th scope="col" className={TH}>Name</th>
                  <th scope="col" className={`${TH} hidden w-[88px] sm:table-cell`}>Value</th>
                  <th scope="col" className={`${TH} hidden w-[88px] sm:table-cell`}>Options</th>
                  <th scope="col" className={`${TH} w-[120px] text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((code) => {
                  const count = optionCount(code);
                  return (
                    <tr key={code._id} className="border-b border-bm-border bg-white transition-colors last:border-b-0 hover:bg-bm-subtle">
                      <td className={`${TD} max-w-0 truncate`} title={code.name}>{code.name}</td>
                      <td className={`${TD} hidden whitespace-nowrap text-bm-muted sm:table-cell`}>{code.value ?? ''}</td>
                      <td className={`${TD} hidden whitespace-nowrap sm:table-cell ${count ? '' : 'text-bm-tertiary'}`}>{count || 'None'}</td>
                      <td className="px-2 py-1.5">
                        <div className="flex items-center justify-end gap-0.5">
                          <IconButton icon="settings" label={`Customize options for ${code.name}`} onClick={() => setShowOptionsDialog(code)} />
                          <IconButton icon="edit" label={`Edit ${code.name}`} onClick={() => handleUpdateCode(code)} />
                          <IconButton icon="trash" label={`Delete ${code.name}`} danger onClick={() => setDeletingCode(code)} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {pageCount > 1 && (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="bm-body-s text-bm-muted">
              {safePage * CODES_PAGE_SIZE + 1} to {Math.min((safePage + 1) * CODES_PAGE_SIZE, shown.length)} of {shown.length}
            </span>
            <ReactPaginate
              previousLabel="← Prev"
              nextLabel="Next →"
              breakLabel="..."
              pageCount={pageCount}
              marginPagesDisplayed={1}
              pageRangeDisplayed={3}
              onPageChange={({ selected }) => setPage(selected)}
              // The Users table class set, plus flex-wrap: a long option has
              // enough page links to overflow the card on a phone.
              containerClassName="flex min-w-0 flex-wrap items-center gap-1 bm-label-xs"
              pageClassName="rounded-full border border-bm-border"
              pageLinkClassName="inline-flex h-8 min-w-[32px] items-center justify-center px-2 text-bm-ink"
              activeClassName="!border-bm-n700 bg-bm-n700 text-white"
              activeLinkClassName="text-white"
              previousClassName="rounded-full border border-bm-border"
              previousLinkClassName="inline-flex h-8 items-center justify-center px-3 text-bm-ink"
              nextClassName="rounded-full border border-bm-border"
              nextLinkClassName="inline-flex h-8 items-center justify-center px-3 text-bm-ink"
              breakLinkClassName="inline-flex h-8 items-center justify-center px-2 text-bm-tertiary"
              disabledClassName="opacity-40 cursor-not-allowed"
              forcePage={safePage}
            />
          </div>
        )}
      </>
    );
  }

  return (
    <Card padding="p-5" className="flex min-w-0 flex-col gap-4">
      <CardHead title={`Codes for ${option.name}`} count={loadFailed || loadingCodes ? undefined : filteredCodes.length}>
        Add, rename, customize or delete the codes in this option.
      </CardHead>

      <AddRow
        value={newCodeName}
        onChange={setNewCodeName}
        onAdd={handleAddCode}
        placeholder="New code name"
        buttonLabel="Add code"
      />

      {body}

      {/* Edit Code Dialog */}
      {editingCode && (
        <BmModal
          title="Edit code"
          width={460}
          onClose={() => setEditingCode(null)}
          footer={
            <>
              <Btn variant="secondary" className="ml-auto" onClick={() => setEditingCode(null)}>
                Cancel
              </Btn>
              <Btn onClick={() => handleConfirmUpdate(true, false, {})} disabled={!editedName.trim()}>
                Save
              </Btn>
            </>
          }
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleConfirmUpdate(true, false, {});
            }}
          >
            <Field label="Name">
              <Input autoFocus value={editedName} onChange={(e) => setEditedName(e.target.value)} />
            </Field>
          </form>
        </BmModal>
      )}

      {deletingCode && (
        <ConfirmModal
          tone="danger"
          title="Delete code"
          body={`Delete "${deletingCode.name}" from ${option.name}? This cannot be undone.`}
          confirmLabel="Delete code"
          onClose={() => setDeletingCode(null)}
          onConfirm={() => {
            handleDeleteCode(deletingCode);
            setDeletingCode(null);
          }}
        />
      )}

      {/* Advanced Option Dialog */}
      <AdvancedOptionEditor
        visible={showOptionsDialog}
        onClose={() => setShowOptionsDialog(null)}
        onSave={(opts) => {
          handleConfirmUpdate(false, true, opts);
        }}
      />
    </Card>
  );
};

export default CodesTable;
